// oxlint-disable import/no-unassigned-import
import Prism from 'prismjs';

// Dependencies first, and the order is load-bearing. A Prism grammar copies from the grammars it
// requires at module-eval time, so one imported before its dependency is built against `undefined`
// and a later import cannot repair it. The failure is quiet — `apex` without `sql` still
// highlights, it just renders inline `[SELECT …]` as a single flat token.
//
// Only these two need hoisting. The `prismjs` main entry above already registers `markup`, `css`,
// `clike` and `javascript`, which covers every other dependency in the list below.
// `languages-test.ts` enforces this ordering.
import 'prismjs/components/prism-sql'; // apex
import 'prismjs/components/prism-typescript'; // tsx
// Grammars, alphabetical. Prism registers these on the shared `Prism.languages` singleton, so
// every consumer of this module gets the same set. Keep in sync with
// `SNIPPET_LANGUAGES` in ./languages — `languages-test.ts` fails on drift either way.
// The exceptions are grammars another grammar depends on (`clike`, `markup-templating`,
// `sql`); those are registered without a public name, and the test derives which ones
// qualify from Prism's own metadata rather than a list kept by hand.
import 'prismjs/components/prism-apex';
import 'prismjs/components/prism-bash';
import 'prismjs/components/prism-brightscript';
import 'prismjs/components/prism-c';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-cpp';
import 'prismjs/components/prism-csharp';
import 'prismjs/components/prism-dart';
import 'prismjs/components/prism-erlang';
import 'prismjs/components/prism-go';
import 'prismjs/components/prism-gradle';
import 'prismjs/components/prism-haskell';
import 'prismjs/components/prism-java';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-kotlin';
import 'prismjs/components/prism-lua';
import 'prismjs/components/prism-makefile';
import 'prismjs/components/prism-markdown';
import 'prismjs/components/prism-markup';
import 'prismjs/components/prism-markup-templating';
import 'prismjs/components/prism-objectivec';
import 'prismjs/components/prism-php';
import 'prismjs/components/prism-powershell';
import 'prismjs/components/prism-python';
import 'prismjs/components/prism-ruby';
import 'prismjs/components/prism-rust';
import 'prismjs/components/prism-swift';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-yaml';
// Plugins. `custom-class` and `command-line` are inert until a consumer opts in
// (by calling `Prism.plugins.customClass.map()` or rendering `<pre class="command-line">`).
import 'prismjs/plugins/command-line/prism-command-line';
import 'prismjs/plugins/custom-class/prism-custom-class';
import 'prismjs/plugins/keep-markup/prism-keep-markup';
import 'prismjs/plugins/line-highlight/prism-line-highlight';
import 'prismjs/plugins/line-numbers/prism-line-numbers';

// Theme and plugin styles. Owned here so consumers don't each carry their own copy.
import 'prism-themes/themes/prism-ghcolors.css';
import 'prismjs/plugins/command-line/prism-command-line.css';
import 'prismjs/plugins/line-numbers/prism-line-numbers.css';
import styles from './codeBlock.module.css';

// Map Prism token classes to CSS module classes
Prism.plugins.customClass.map({
  operator: styles.operator,
  punctuation: styles.punctuation,
  function: styles.function,
  tag: styles.tag,
  'attr-value': styles.attrValue,
  string: styles.string,
  property: styles.property,
  keyword: styles.keyword,
});

// Prism's bash grammar recognizes `npm`, `pnpm`, `yarn` and `node`, but not `npx`, so an
// `npx …` snippet renders as flat text beside snippets that highlight. Alias it to `function`
// so it takes the same colour as the package managers already in the grammar.
Prism.languages.insertBefore('bash', 'function', {
  'npx-command': { pattern: /\bnpx\b/, alias: 'function' },
});

export const highlightElement = (element: Element) => {
  Prism.highlightElement(element);
};
